import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { DialogService } from 'primeng/dynamicdialog';

import { DashboardServiceService } from '@admin/shared/services/dashboard-service.service';

import { CreateWorkspaceDialogComponent } from '@common/popups/create-workspace-dialog/create-workspace-dialog.component';

import { BillingService } from '@core/services/billing.service';
import { UserProfileService } from '@core/services/user.service';
import { AiCreatePostModalService } from '@features/posts/services/ai-create-post-modal.service';
import { BillingPlan } from '@shared/models/billing-plan.model';
import { shouldShowUpgradeNav, sortPlans } from '@shared/utils/plan.utils';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
  standalone: false,
})
export class SidebarComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  isPlannerActive = false;
  isPostQueueActive = false;
  isAIActive = false;
  selectedWorkspace: any;
  preventAbuse = false;
  billingPlans: BillingPlan[] = [];
  menuItems = [
    {
      label: 'Workspace Settings',
    },
    {
      label: 'Help & Support',
    },
  ];
  workspaces: any[] | undefined = [];
  userData: any;

  createPostItems = [
    {
      label: 'Create Post',
      icon: '/assets/images/menu/list.svg',
      command: () => {
        this.router.navigate(['/create']);
      },
    },
    {
      label: 'Posts Queue',
      icon: '/assets/images/menu/calendar.svg',
      command: () => {
        this.router.navigate(['/post-schedule']);
      },
    },
  ];
  scheduleItems = [
    {
      label: 'Posts List',
      icon: '/assets/images/menu/list.svg',
      command: () => {
        this.router.navigate(['/planner/list']);
      },
    },
    {
      label: 'Calendar',
      icon: '/assets/images/menu/calendar.svg',
      command: () => {
        this.router.navigate(['/planner/calendar']);
      },
    },
    {
      label: 'Kanban',
      icon: '/assets/images/menu/todos.svg',
      command: () => {
        this.router.navigate(['/planner/calendar']);
      },
    },
  ];

  aiSubmenu = [
    {
      label: 'AI Prompts',
      icon: '/assets/images/menu/list.svg',
      command: () => {
        this.router.navigate(['/ai/ai-writer']);
      },
    },
    {
      label: 'AI Chat',
      icon: '/assets/images/menu/calendar.svg',
      command: () => {
        this.router.navigate(['/ai/ai-chat']);
      },
    },
  ];

  /*  workspaceMenu = [
    {
      label: 'Settings',
      icon: '/assets/images/menu/setting.svg',
      command: () => {
        this.router.navigate(['/workspace/settings']);
      },
      isWorkspace: false,
      isInTrial: false,
      isLifetimeFreeTrial: false,
      has_expired_trial: false,
    },
  ];*/
  workspaceMenu: any[] = [];
  constructor(
    protected router: Router,
    private dashboardService: DashboardServiceService,
    private billingService: BillingService,
    private userProfileService: UserProfileService,
    private dialogService: DialogService,
    private aiCreatePostModalService: AiCreatePostModalService
  ) {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        // Check if the URL includes '/planner' to set the active state
        this.isPlannerActive = this.router.url.startsWith('/planner');
        this.isAIActive = this.router.url.startsWith('/ai');
        this.isPostQueueActive = this.router.url.startsWith('/post-schedule');
      }
    });
  }

  ngOnInit() {
    this.isPlannerActive = this.router.url.startsWith('/planner');
    this.isAIActive = this.router.url.startsWith('/ai');

    this.billingService
      .getBillingPlans()
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        this.billingPlans = sortPlans(response.plans);
      });

    this.dashboardService.getWorkspace().subscribe({
      next: (res) => {
        this.selectedWorkspace = res;
      },
    });

    this.userProfileService.getUser().subscribe({
      next: (res) => {
        this.workspaceMenu = [];
        if (res) {
          res.workspaces.forEach((workspace: any) => {
            this.workspaceMenu.push({
              label: workspace.name,
              icon: workspace.hex_color,
              command: () => {
                this.changeWorkspace(workspace);
              },
              isWorkspace: true,
              isInTrial: workspace.planInfo.trialInfo.isInTrial,
              isLifetimeFreeTrial: workspace.planInfo.trialInfo.isLifetimeFreeTrial,
              has_expired_trial: workspace.planInfo.trialInfo.has_expired_trial,
            });
          });
          this.userData = res.user;
        }
      },
    });
  }

  changeWorkspace(workspace: any) {
    this.preventAbuse = true;
    this.dashboardService.switchWorkspace(workspace.uuid).subscribe({
      next: () => {
        this.dashboardService.setWorkspace(workspace);
        this.userProfileService.getUserDetails().subscribe();
        this.preventAbuse = false;
        this.router.navigate(['/']);
      },
      error: () => {
        this.preventAbuse = false;
      },
    });
  }

  createNewWorkspace() {
    this.dialogService.open(CreateWorkspaceDialogComponent, {
      breakpoints: { '1199px': '75vw', '575px': '90vw' },
    });
  }

  openCreatePostModal() {
    this.aiCreatePostModalService.openModal();
  }

  isConnectionsActive(): boolean {
    return (
      this.router.url.includes('/workspace/settings') &&
      this.router.url.includes('selectedTab=connected-accounts')
    );
  }

  showUpgradeNav(): boolean {
    return shouldShowUpgradeNav(this.selectedWorkspace, this.billingPlans);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
